import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from './AppShell';
import { AuthProvider } from '@/shared/auth/AuthProvider';
import { AuthGuard } from '@/shared/auth/AuthGuard';

// Auth pages (lazy-loaded, no AppShell)
const LoginPage = lazy(() => import('../modules/auth/LoginPage'));
const SignUpPage = lazy(() => import('../modules/auth/SignUpPage'));
const ForgotPasswordPage = lazy(() => import('../modules/auth/ForgotPasswordPage'));
const MfaSetupPage = lazy(() => import('../modules/auth/MfaSetupPage'));

// App modules (lazy-loaded, inside AppShell)
const EmailModule = lazy(() => import('../modules/email/EmailModule'));
const ContactsModule = lazy(() => import('../modules/contacts/ContactsModule'));
const CalendarModule = lazy(() => import('../modules/calendar/CalendarModule'));
const AdminModule = lazy(() => import('../modules/admin/AdminModule'));
const SettingsModule = lazy(() => import('../modules/settings/SettingsModule'));

// Public pages (no auth)
const SecureViewer = lazy(() => import('../modules/secure/SecureViewerPage'));

function LoadingFallback() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      color: 'var(--color-text-secondary)',
    }}>
      Loading...
    </div>
  );
}

export function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const rtlLocales = ['ar', 'fa'];
    const isRtl = rtlLocales.includes(i18n.language);
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <AuthProvider>
      <Routes>
        {/* Auth routes — no AppShell */}
        <Route path="/auth/login" element={
          <Suspense fallback={<LoadingFallback />}><LoginPage /></Suspense>
        } />
        <Route path="/auth/signup" element={
          <Suspense fallback={<LoadingFallback />}><SignUpPage /></Suspense>
        } />
        <Route path="/auth/forgot-password" element={
          <Suspense fallback={<LoadingFallback />}><ForgotPasswordPage /></Suspense>
        } />
        <Route path="/auth/mfa-setup" element={
          <Suspense fallback={<LoadingFallback />}><MfaSetupPage /></Suspense>
        } />
        <Route path="/auth/callback" element={<Navigate to="/mail" replace />} />

        {/* Secure message viewer — public, no auth required */}
        <Route path="/secure/:token" element={
          <Suspense fallback={<LoadingFallback />}><SecureViewer /></Suspense>
        } />

        {/* Protected routes — inside AppShell + AuthGuard */}
        <Route element={
          <AuthGuard>
            <AppShell />
          </AuthGuard>
        }>
          <Route path="/mail/*" element={
            <Suspense fallback={<LoadingFallback />}><EmailModule /></Suspense>
          } />
          <Route path="/contacts/*" element={
            <Suspense fallback={<LoadingFallback />}><ContactsModule /></Suspense>
          } />
          <Route path="/calendar/*" element={
            <Suspense fallback={<LoadingFallback />}><CalendarModule /></Suspense>
          } />
          <Route path="/admin/*" element={
            <Suspense fallback={<LoadingFallback />}><AdminModule /></Suspense>
          } />
          <Route path="/settings/*" element={
            <Suspense fallback={<LoadingFallback />}><SettingsModule /></Suspense>
          } />
          <Route path="/" element={<Navigate to="/mail" replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}
